/**
 * DecisionFlow — AIGovernancePanel
 * ui/src/components/admin/AIGovernancePanel.jsx
 * S1034 — Agent Operating Mode — Comp Admin Trust Center Panel
 *
 * Slotted into TrustCenter.jsx alongside existing governance panels.
 * Visible to comp_admin ONLY when:
 *   ca_permitted_modes is non-empty OR ca_kill_switch_enabled = TRUE
 * If CA has no delegation: panel is not rendered at all.
 * No "you don't have access" message — panel simply does not appear.
 * spec_agent_operating_mode_s1033.md §8.3
 *
 * Endpoints:
 *   GET  /api/v1/tenant-ai-mode            — read mode state + recent changes
 *   PUT  /api/v1/tenant-ai-mode            — switch current_mode (T1/T2)
 *   POST /api/v1/tenant-ai-mode/kill-switch — activate/deactivate kill switch
 *
 * Design rules (non-negotiable):
 *   rgb(var(--color-*)) throughout — no hardcoded hex, no slate/emerald/red/amber classes
 *   text-tok-* sizes only
 *   Modal does NOT close on outside click (NN-07)
 *   reason field always required
 *
 * Usage in TrustCenter.jsx:
 *   import AIGovernancePanel from './AIGovernancePanel.jsx'
 *   // In sidebar Cards section:
 *   <AIGovernancePanel />
 */

import { useCallback, useEffect, useState } from "react";
import {
  Bot,
  CheckCircle2,
  ShieldCheck,
  ShieldOff,
} from "lucide-react";

import Card from "../shared/Card.jsx";

const API_BASE = "/api/v1/tenant-ai-mode";
const REASON_MIN = 10;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatDateTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-US", {
      month: "short", day: "numeric", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

const MODE_LABELS = {
  T0: "Agent Off",
  T1: "Advise Only",
  T2: "Full Copilot",
};

const CHANGE_TYPE_LABELS = {
  mode_change:       "Mode change",
  kill_switch_on:    "Kill switch on",
  kill_switch_off:   "Kill switch off",
  ceiling_change:    "Ceiling change",
  delegation_change: "Delegation change",
};

// ---------------------------------------------------------------------------
// Agent status badge — rgb(var(--color-*)) only
// ---------------------------------------------------------------------------

function AgentStatusBadge({ isActive }) {
  if (isActive) {
    return (
      <div className="flex items-center gap-2 rounded-xl border border-[rgb(var(--color-danger))]/40 bg-[rgb(var(--color-danger))]/8 px-3 py-2.5">
        <ShieldOff className="h-4 w-4 shrink-0 text-[rgb(var(--color-danger))]" />
        <span className="text-tok-sm font-medium text-[rgb(var(--color-danger))]">
          Agent halted — Kill switch active
        </span>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-2 rounded-xl border border-[rgb(var(--color-success))]/40 bg-[rgb(var(--color-success))]/8 px-3 py-2.5">
      <ShieldCheck className="h-4 w-4 shrink-0 text-[rgb(var(--color-success))]" />
      <span className="text-tok-sm font-medium text-[rgb(var(--color-success))]">
        Agent operating normally
      </span>
    </div>
  );
}

function ModePill({ mode }) {
  const tones = {
    T0: "text-[rgb(var(--color-danger))] bg-[rgb(var(--color-danger))]/8 border-[rgb(var(--color-danger))]/30",
    T1: "text-[rgb(var(--color-warning))] bg-[rgb(var(--color-warning))]/8 border-[rgb(var(--color-warning))]/30",
    T2: "text-[rgb(var(--color-success))] bg-[rgb(var(--color-success))]/8 border-[rgb(var(--color-success))]/30",
  };
  return (
    <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-tok-xs font-bold ${tones[mode] ?? tones.T2}`}>
      {MODE_LABELS[mode] ?? mode}
    </span>
  );
}

// ---------------------------------------------------------------------------
// ReasonModal — rgb(var(--color-*)) only
// ---------------------------------------------------------------------------

function ReasonModal({ title, description, confirmLabel, confirmTone, onConfirm, onCancel, saving }) {
  const [reason, setReason] = useState("");
  const canSubmit = reason.trim().length >= REASON_MIN && !saving;

  const confirmCls = {
    danger:  "bg-[rgb(var(--color-danger))] hover:opacity-90 text-white",
    success: "bg-[rgb(var(--color-success))] hover:opacity-90 text-white",
    primary: "bg-[rgb(var(--color-primary))] hover:opacity-90 text-white",
  }[confirmTone] ?? "bg-[rgb(var(--color-primary))] hover:opacity-90 text-white";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-md rounded-2xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] p-6 shadow-xl">
        <p className="mb-1 text-tok-sm font-bold text-[rgb(var(--color-text))]">{title}</p>
        {description && (
          <p className="mb-4 text-tok-xs text-[rgb(var(--color-text-muted))]">{description}</p>
        )}
        <div className="mb-4 space-y-1">
          <label className="text-tok-xs font-medium text-[rgb(var(--color-text-muted))]">
            Reason <span className="text-[rgb(var(--color-danger))]">*</span>
          </label>
          <textarea
            rows={3}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Required — stored in audit trail…"
            disabled={saving}
            className="w-full resize-none rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-3 py-2 text-tok-sm text-[rgb(var(--color-text))] placeholder:text-[rgb(var(--color-text-muted))] focus:outline-none focus:ring-1 focus:ring-[rgb(var(--color-primary))] disabled:opacity-50"
          />
          <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">
            {reason.trim().length}/{REASON_MIN} characters minimum
          </p>
        </div>
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={saving}
            className="rounded-xl border border-[rgb(var(--color-border))] px-4 py-2 text-tok-sm font-medium text-[rgb(var(--color-text-muted))] hover:bg-[rgb(var(--color-surface-muted))] disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => onConfirm(reason.trim())}
            disabled={!canSubmit}
            className={`rounded-xl px-4 py-2 text-tok-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed transition-opacity ${confirmCls}`}
          >
            {saving ? "Saving…" : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// AIGovernancePanel — main export
// ---------------------------------------------------------------------------

export default function AIGovernancePanel() {
  const [modeData, setModeData] = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState(null);
  const [toast,    setToast]    = useState(null);

  // modal: 'mode_t1' | 'mode_t2' | 'ks_activate' | 'ks_deactivate' | null
  const [modal,  setModal]  = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res  = await fetch(API_BASE, { credentials: "include" });
      const body = await res.json();
      if (!res.ok) throw new Error(body?.error?.message ?? `HTTP ${res.status}`);
      setModeData(body);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(t);
  }, [toast]);

  // If no delegation at all, render nothing — spec §8.3.
  if (!loading && !error && modeData) {
    const hasPermission =
      (modeData.ca_permitted_modes?.length > 0) || modeData.ca_kill_switch_enabled;
    if (!hasPermission) return null;
  }

  async function handleModeSwitch(targetMode, reason) {
    setSaving(true);
    try {
      const res = await fetch(API_BASE, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ current_mode: targetMode, reason }),
      });
      const body = await res.json();
      if (!res.ok) throw new Error(body?.error?.message ?? `Error ${res.status}`);
      setToast(`Mode changed to ${MODE_LABELS[targetMode]}.`);
      setModal(null);
      await load();
    } catch (e) {
      setToast(`Error: ${e.message}`);
      setModal(null);
    } finally {
      setSaving(false);
    }
  }

  async function handleKillSwitch(action, reason) {
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/kill-switch`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action, reason }),
      });
      const body = await res.json();
      if (!res.ok) throw new Error(body?.error?.message ?? `Error ${res.status}`);
      setToast(action === "activate" ? "Agent halted." : "Agent resumed.");
      setModal(null);
      await load();
    } catch (e) {
      setToast(`Error: ${e.message}`);
      setModal(null);
    } finally {
      setSaving(false);
    }
  }

  const isActive  = modeData?.is_kill_switch_active ?? false;
  const currMode  = modeData?.current_mode ?? "T2";
  const permitted = modeData?.ca_permitted_modes ?? [];
  const ksEnabled = modeData?.ca_kill_switch_enabled ?? false;
  const changes   = modeData?.recent_changes ?? [];

  const modalConfig = {
    mode_t1: {
      title:        "Switch to Advise Only?",
      description:  "Agent will read and explain platform state only. No proposals or write actions.",
      confirmLabel: "Switch to Advise Only",
      confirmTone:  "primary",
      onConfirm:    (r) => handleModeSwitch("T1", r),
    },
    mode_t2: {
      title:        "Switch to Full Copilot?",
      description:  "Agent will have full proposal and write-action capability.",
      confirmLabel: "Switch to Full Copilot",
      confirmTone:  "success",
      onConfirm:    (r) => handleModeSwitch("T2", r),
    },
    ks_activate: {
      title:        "Halt agent for this tenant?",
      description:  "All new sessions will be rejected immediately. Operating mode is preserved.",
      confirmLabel: "Halt Agent",
      confirmTone:  "danger",
      onConfirm:    (r) => handleKillSwitch("activate", r),
    },
    ks_deactivate: {
      title:        "Resume agent?",
      description:  "Agent will resume in its current operating mode immediately.",
      confirmLabel: "Resume Agent",
      confirmTone:  "success",
      onConfirm:    (r) => handleKillSwitch("deactivate", r),
    },
  };

  return (
    <>
      {/* Toast */}
      {toast && (
        <div className="fixed right-5 top-5 z-50 max-w-sm rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-4 py-3 shadow-lg text-tok-sm text-[rgb(var(--color-text))]">
          {toast}
        </div>
      )}

      {/* Modal */}
      {modal && modalConfig[modal] && (
        <ReasonModal
          {...modalConfig[modal]}
          saving={saving}
          onCancel={() => setModal(null)}
        />
      )}

      <Card
        title="AI Governance"
        action={<Bot className="h-4 w-4 text-[rgb(var(--color-text-muted))]" />}
      >
        {loading && (
          <div className="animate-pulse space-y-2">
            <div className="h-10 w-full rounded skeleton" />
            <div className="h-4 w-2/3 rounded skeleton" />
          </div>
        )}

        {error && !loading && (
          <div className="rounded-xl bg-[rgb(var(--color-surface-muted))] px-3 py-2 text-tok-sm text-[rgb(var(--color-danger))]">
            {error}
          </div>
        )}

        {modeData && !loading && (
          <div className="space-y-4">

            {/* Agent status */}
            <AgentStatusBadge isActive={isActive} />

            {/* Current mode */}
            <div className="flex items-center justify-between">
              <p className="text-tok-xs font-medium text-[rgb(var(--color-text-muted))]">
                Operating mode
              </p>
              <ModePill mode={currMode} />
            </div>

            {/* Kill switch control */}
            {ksEnabled && (
              <div>
                {isActive ? (
                  <button
                    type="button"
                    onClick={() => setModal("ks_deactivate")}
                    className="flex w-full items-center justify-center gap-2 rounded-xl border border-[rgb(var(--color-success))]/40 bg-[rgb(var(--color-success))]/8 px-4 py-2.5 text-tok-sm font-semibold text-[rgb(var(--color-success))] hover:bg-[rgb(var(--color-success))]/15 transition-colors"
                  >
                    <ShieldCheck className="h-4 w-4" />
                    Resume Agent
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => setModal("ks_activate")}
                    className="flex w-full items-center justify-center gap-2 rounded-xl border border-[rgb(var(--color-danger))]/40 bg-[rgb(var(--color-danger))]/8 px-4 py-2.5 text-tok-sm font-semibold text-[rgb(var(--color-danger))] hover:bg-[rgb(var(--color-danger))]/15 transition-colors"
                  >
                    <ShieldOff className="h-4 w-4" />
                    Halt Agent
                  </button>
                )}
              </div>
            )}

            {/* Mode selector — only when not halted and has permitted modes */}
            {!isActive && permitted.length > 0 && (
              <div className="space-y-2">
                <p className="text-tok-xs font-medium text-[rgb(var(--color-text-muted))]">
                  Switch mode
                </p>
                <div className="flex gap-2">
                  {permitted.map((mode) => (
                    <button
                      key={mode}
                      type="button"
                      onClick={() => setModal(`mode_${mode.toLowerCase()}`)}
                      disabled={mode === currMode}
                      className={`flex-1 rounded-xl border px-3 py-2 text-tok-xs font-semibold transition disabled:cursor-default ${
                        mode === currMode
                          ? "border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] text-[rgb(var(--color-text-muted))]"
                          : "border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] text-[rgb(var(--color-text))] hover:border-[rgb(var(--color-primary))] hover:bg-[rgb(var(--color-primary))]/5 hover:text-[rgb(var(--color-primary))]"
                      }`}
                    >
                      {mode === currMode ? `${MODE_LABELS[mode]} (current)` : MODE_LABELS[mode]}
                    </button>
                  ))}
                </div>
                {isActive && (
                  <p className="text-tok-xs text-[rgb(var(--color-warning))] font-medium">
                    Mode switching unavailable while agent is halted.
                  </p>
                )}
              </div>
            )}

            {/* Recent changes — last 3 */}
            {changes.length > 0 && (
              <div className="space-y-1.5">
                <p className="text-tok-xs font-medium text-[rgb(var(--color-text-muted))]">
                  Recent changes
                </p>
                <div className="space-y-1">
                  {changes.slice(0, 3).map((c) => (
                    <div key={c.change_id} className="flex items-center justify-between gap-2 text-tok-xs">
                      <span className="text-[rgb(var(--color-text))]">
                        {CHANGE_TYPE_LABELS[c.change_type] ?? c.change_type}
                      </span>
                      <span className="text-[rgb(var(--color-text-muted))] whitespace-nowrap">
                        {formatDateTime(c.changed_at)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </Card>
    </>
  );
}
